import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Padding } from 'src/styles/paddingComponent'
import { z } from 'zod'
import { useGetCustomerUsers } from '../../api/customer'
import { CoordinateFormField } from '../../components/CoordinateFormField'
import { AppForm, AppFormProps, FormImageFileUpload, FormSelectField, FormTextField } from '../../components/FormCore'
import { GenericDialogLayout } from '../../components/GenericDialogLayout'
import { MainButton } from '../../components/MainButton'
import { ServiceStationType } from '../../enums'
import { useZodForm } from '../../hooks/zod-form'
import { UserDetail, getUserDetail } from '../../utils/getUserDetails'

export interface ServiceStationFormProps extends AppFormProps {
  customerId: EntityId
  title: string
  pop: () => void
  onSubmit: (form: any) => void
  isLoading?: boolean
}
export const ServiceStationFrom: React.FC<ServiceStationFormProps> = ({
  onSubmit,
  initialValues,
  customerId,
  title,
  pop,
  isLoading,
}) => {
  const { t: translate } = useTranslation(['product', 'common'])
  const { data: customerUsers } = useGetCustomerUsers(customerId)

  const schema = z.object({
    name: z.string().nonempty(translate('nameIsRequired', { ns: 'service-station' })),
    address: z.string().or(z.null()).optional(),
    serviceStationNumber: z.string().optional(),
    type: z.number(),
    photoId: z.number().or(z.null()).optional(),
    latitude: z
      .string()
      .optional()
      .transform((val, ctx) => {
        if (val) {
          const returnVal = parseFloat(val)
          if (returnVal < -90 || returnVal > 90) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: translate('latitude_range_error', { ns: 'common' }) })
          }
          return returnVal
        }
        return null
      })
      .or(z.null())
      .or(z.number().min(-90).max(90).optional()),
    longitude: z
      .string()
      .optional()
      .transform((val, ctx) => {
        if (val) {
          const returnVal = parseFloat(val)
          if (returnVal < -180 || returnVal > 180) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: translate('longitude_range_error', { ns: 'common' }) })
          }
          return returnVal
        }
        return null
      })
      .or(z.null())
      .or(z.number().min(-180).max(180).optional()),
    managedByUserId: z
      .object({
        id: z.string().optional(),
      })
      .transform(value => (value ? value.id : value))
      .optional()
      .nullable(),
    customerId: z.number(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      type: typeof initialValues?.type == 'string' ? ServiceStationType.ServiceStation : initialValues?.type,
      managedByUserId: initialValues?.managedByUser,
      customerId: customerId,
    },
  })

  const serviceStationTypes = Object.keys(ServiceStationType)
    .filter(v => isNaN(Number(v)))
    .map(t => ServiceStationType[t])

  return (
    <GenericDialogLayout
      title={title}
      pop={pop}
      removedCloseButton={false}
      actions={[
        <MainButton
          key="locality-form-button"
          variant="contained"
          disabled={isLoading}
          onClick={() => {
            form.handleSubmit(onSubmit)()
          }}
          data-cy="UpdateLocality_submit"
        >
          {initialValues?.id ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} hasInitialValues={initialValues}>
        <FormTextField name="name" label={translate('serviceStationName', { ns: 'service-station' })} />
        <FormTextField name="address" label={translate('address', { ns: 'common' })} />
        <FormTextField
          name="serviceStationNumber"
          label={translate('serviceStationNumber', { ns: 'service-station' })}
        />
        <CoordinateFormField
          direction="latitude"
          type="number"
          name="latitude"
          label={translate('latitude', { ns: 'common' })}
        />
        <CoordinateFormField
          direction="longitude"
          type="number"
          name="longitude"
          label={translate('longitude', { ns: 'common' })}
        />

        <FormSelectField
          name="managedByUserId"
          data={customerUsers!}
          label={translate('managedByUserId', { ns: 'service-station' })}
          getOptionLabel={option => getUserDetail(option, UserDetail.NameAndEmail)}
          renderOption={(props: any, option) => (
            <Box {...props} key={option}>
              {getUserDetail(option, UserDetail.NameAndEmail)}
            </Box>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
        <Padding.pl6>
          <FormImageFileUpload
            label={translate('service-station-photo', { ns: 'common' })}
            name="photoId"
            mapFileKey="id"
            initialFiles={initialValues?.photo}
            inactiveMsg={translate('chooseCertificate', { ns: 'locality' })}
          />
        </Padding.pl6>
      </AppForm>
    </GenericDialogLayout>
  )
}

export default ServiceStationFrom
